import { useEffect, useState } from "react";

import {
  Button,
  RadioButton,
  TextArea,
  TextField,
  Table,
  TableMetadata,
  Typography,
  useTable,
  AutocompleteMultipleSelect,
  Label,
  InfoPanel,
} from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Row, Col, Container, Modal as BSModal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { DatePickerRange } from "./components";
import { DEFAULT_INSTITUTION_COMMITTEE } from "./constants";
import {
  canEmployeeBeRemoved,
  getCommitteeEmployeeDisplayName,
  isEmployeeActive,
  updateEmployeesWithPresident,
  updateCommitteeEmployee,
  setupCommitteeEmployees,
} from "./utils";
import {
  getInstitutionCommitteeById,
  createInstitutionCommitteeById,
  searchInstitutionCommitteeEmployee,
  saveInstitutionCommittee,
  getInstitutionCommitteeEmployeeOib,
} from "../../api/Committee/InstitutionCommitteeApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  FormCard,
  ToolbarEdit,
  ToolbarTop,
} from "../../components";
import { OIB_REGEX } from "../../constants";
import { useEnumCodeBooks } from "../../hooks/useEnumCodeBooks";
import { ValidationFailure } from "../../models";
import {
  CommitteeEmployee,
  OibEmployee,
  CommitteeTypeEnum,
  InstitutionCommittee,
} from "../../models/Committee";
import { CodeBookPagedFilter, SaveResult } from "../../models/Shared";
import { committeeDetailUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";
import { FormatUtils, MsgUtils } from "../../utils";
import { useDeleteCommittee } from "./hooks/useDeleteCommittee";

export function CommitteeEdit() {
  const { id, institutionId } = useParams<{
    id: string;
    institutionId: string;
  }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [item, setItem] = useState<InstitutionCommittee>(
    DEFAULT_INSTITUTION_COMMITTEE
  );
  const [tableData, setTableData] = useState<CommitteeEmployee[]>([]);
  const tableMetadata: TableMetadata<CommitteeEmployee> = {
    rowKey: "rowKeyId",
    columns: [
      {
        headerClassName: "col-4",
        columnKey: "expertCommissionRole",
        header: t("common.expertCommission.role"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{item.committeeRole?.name}</div>;
        },
      },
      {
        headerClassName: "col-6",
        columnKey: "employee",
        header: t("common.expertCommission.employee"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{getCommitteeEmployeeDisplayName(item)}</div>;
        },
      },
      {
        headerClassName: "col-4",
        columnKey: "activeTo",
        header: t("common.expertCommission.activeTo"),
        accessor: (item) => {
          return <div>{FormatUtils.formatDate(item.deactivationDate)}</div>;
        },
      },
      {
        columnKey: "edit",
        header: t("common.edit"),
        headerClassName: "col-1",
        accessor: (employee) => {
          return (
            <div>
              <Button
                size="sm"
                outline
                onClick={() => handleDetailsClicked(employee)}
                disabled={
                  employee.committeeRole.committeeType !==
                  CommitteeTypeEnum.Institution
                }
              >
                {t("common.edit")}
              </Button>
            </div>
          );
        },
      },
      {
        columnKey: "president",
        header: t("common.expertCommission.president"),
        headerClassName: "col-1",
        accessor: (emp: CommitteeEmployee) => {
          const isActive = isEmployeeActive(emp);

          return (
            <div className="pt-2 ml-3">
              <RadioButton
                ariaLabel="item.president"
                key={emp.rowKeyId}
                name={"item.president"}
                checked={emp.isPresident}
                onChange={() => handlePresidentChange(emp.rowKeyId!)}
                value={emp.rowKeyId!}
                disabled={
                  !(
                    emp.employee &&
                    emp.committeeRole.committeeType ===
                      CommitteeTypeEnum.Institution
                  ) || !isActive
                }
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };

  const [tableProps] = useTable({
    metadata: tableMetadata,
    data: tableData,
    pagination: false,
    totalItems: 1,
    defaultSort: {},
  });
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [filteredEmployees, setFilteredEmployees] = useState<CodeBook[]>([]);

  const { committeeStatusCodeBook: statuses } = useEnumCodeBooks();
  const [editing, setEditing] = useState<CommitteeEmployee | null>(null);
  const [employee, setEmployee] = useState<CodeBook | null | undefined>(null);
  const [oibEmployee, setOibEmployee] = useState<OibEmployee | null>(null);
  const [editedActiveTo, setEditedActiveTo] = useState<Date | null>(null);
  const [showOibAlert, setShowOibAlert] = useState<string | null>(null);

  const { handleDeleteCommittee, isDeleteEnabled } = useDeleteCommittee({
    isEnabled: !item.hasDecisions,
    committeeType: "institution",
    committeeId: +id,
  });

  useEffect(() => {
    (async () => {
      if (!isNaN(+id) && +id > 0) {
        const result = await getInstitutionCommitteeById(+id);
        if (result) {
          setupCommitteeEmployees(result, setItem, setTableData);
        }
      }
      if (!isNaN(+institutionId) && +institutionId > 0) {
        const result = await createInstitutionCommitteeById(+institutionId);
        if (result) {
          setupCommitteeEmployees(result, setItem, setTableData);
        }
      }
    })();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, institutionId]);

  const handleDetailsClicked = (
    expertCommissionEmployee: CommitteeEmployee
  ) => {
    setEditing(expertCommissionEmployee);
    setEmployee(expertCommissionEmployee.employee);
    setOibEmployee(expertCommissionEmployee.oibEmployee);

    setEditedActiveTo(
      expertCommissionEmployee.deactivationDate
        ? new Date(expertCommissionEmployee.deactivationDate)
        : null
    );
  };
  const handleEmployeeFilterChange = async (filter: string) => {
    const search: CodeBookPagedFilter = {
      pageSize: 100,
      page: 1,
      search: filter,
    };

    const result = await searchInstitutionCommitteeEmployee(search);
    setFilteredEmployees(result.results);
  };
  const handleEmployeeChangeMultiselect = (e: any) => {
    if (!canEmployeeBeRemoved(editedActiveTo, editing)) {
      return;
    }

    const employeeList = e.target.value as CodeBook[];
    const employeeCodeBook =
      employeeList.length > 0 ? employeeList[0] : undefined;
    setEmployee(employeeCodeBook);
    if (employeeCodeBook) {
      setOibEmployee(null);
    }
  };

  const handlePresidentChange = (rowKeyId: CommitteeEmployee["rowKeyId"]) => {
    const employees = updateEmployeesWithPresident(tableData, rowKeyId);

    setTableData(employees);
    const newItem: InstitutionCommittee = {
      ...item,
      employees: employees,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleNoteChange = (e: any) => {
    const note = e.target.value;
    const newItem: InstitutionCommittee = {
      ...item,
      note: note,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleOibChanged = (e: any) => {
    const oib = e.target.value as string;
    const newOibEmployee: OibEmployee = {
      ...oibEmployee,
      oib: oib,
      lastName: undefined,
    };
    setOibEmployee(newOibEmployee);
    setShowOibAlert(null);
  };
  const handleOibSystemClick = async () => {
    if (oibEmployee?.oib) {
      const result = await getInstitutionCommitteeEmployeeOib(oibEmployee.oib);
      if (result && result.item && result.item.oib) {
        const newOibEmployee: OibEmployee = {
          ...oibEmployee,
          firstName: result.item.firstName,
          lastName: result.item.lastName,
        };
        setOibEmployee(newOibEmployee);
        setEmployee(undefined);
      } else {
        setShowOibAlert(t(MsgUtils.getOibErrorMessage(result.status)));
      }
    }
  };

  const onModalClose = () => {
    setEditedActiveTo(null);
    setEditing(null);
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={"hide"}
        pageTitle={t("common.expertCommission.pageNameEdit")}
        errors={errors}
      />

      <FormCard>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.institution")}
            </Typography>
          </Col>
          <Col md={10}>
            <Typography variant="base" color="light">
              {item.institution?.code} - {item.institution?.name}
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.dateFromDateTo")}
            </Typography>
          </Col>
          <Col md={4}>
            <Typography variant="base" color="light">
              {item.dateFrom ? hrDate(new Date(item.dateFrom)) : ""} -{" "}
              {item.dateTo ? hrDate(new Date(item.dateTo)) : ""}
            </Typography>
          </Col>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.status")}
            </Typography>
          </Col>
          <Col md={4}>
            <Label
              color={FormatUtils.getCommitteeStatusColor(item.statusId)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {statuses.find((v) => v.id === item.statusId)?.name}
            </Label>
          </Col>
        </Row>
      </FormCard>

      <FormCard>
        <Table {...tableProps} />
      </FormCard>

      <FormCard>
        <Row>
          <Col md={12}>
            <TextArea
              ariaLabel="item.note"
              label={t("common.expertCommission.note")}
              name={"item.note"}
              value={item.note ?? ""}
              onChange={handleNoteChange}
              errorText={
                errors?.find((e) => e.propertyName === "Note")?.message
              }
              maxLength={5000}
            />
          </Col>
        </Row>
      </FormCard>

      <ToolbarEdit
        showCancel={"show"}
        showSave={"show"}
        handleSave={async () => saveInstitutionCommittee(item)}
        redirectUrlSaveOK={committeeDetailUrl}
        setSaveErrors={setErrors}
        onSaveSuccess={(data: SaveResult<InstitutionCommittee>) => {
          if (!data.result) return;

          setItem(data.result);
        }}
        showDelete={isDeleteEnabled ? "show" : "disable"}
        onClickDelete={handleDeleteCommittee}
      />
      <BSModal
        size="lg"
        backdrop="static"
        centered
        show={editing != null}
        onConfirm={onModalClose}
        onClose={onModalClose}
        onHide={onModalClose}
      >
        <BSModal.Header>{editing?.committeeRole!.name}</BSModal.Header>
        <BSModal.Body>
          <Container>
            {showOibAlert === null || (
              <InfoPanel
                color="danger"
                message=""
                dismissible={false}
                fullWidth={true}
              >
                {showOibAlert}
              </InfoPanel>
            )}
            <Row>
              <Col md={9}>
                <AutocompleteMultipleSelect
                  label={t("common.expertCommission.employee")}
                  name={"employeeId"}
                  value={employee?.id !== undefined ? [employee] : []}
                  valueProp={"id"}
                  textProp={"name"}
                  onChange={handleEmployeeChangeMultiselect}
                  selectOptions={filteredEmployees}
                  onFilterChange={handleEmployeeFilterChange}
                  maximumSelectedItems={1}
                />
              </Col>
            </Row>
            {editing?.committeeRole.checkOIB && (
              <>
                <Row>
                  <Col md={8}>
                    <TextField
                      label={t("common.schoolCandidateModel.oib")}
                      value={oibEmployee?.oib}
                      onChange={handleOibChanged}
                      disabled={employee?.id !== undefined}
                    />
                  </Col>
                  <Col md={4}>
                    <br />
                    <Button
                      onClick={handleOibSystemClick}
                      disabled={
                        !(
                          oibEmployee?.oib !== undefined &&
                          OIB_REGEX.test(oibEmployee.oib)
                        )
                      }
                      size="sm"
                    >
                      {t("common.schoolCandidateModel.connectOibSystem")}
                    </Button>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <div>
                      {oibEmployee?.lastName !== undefined
                        ? `${oibEmployee?.oib}, ${oibEmployee?.lastName}, ${oibEmployee?.firstName}`
                        : ""}
                    </div>
                  </Col>
                </Row>
              </>
            )}
            <DatePickerRange
              onChange={setEditedActiveTo}
              value={editedActiveTo}
              disabled={!employee || editing?.id === 0}
            />
          </Container>
        </BSModal.Body>
        <BSModal.Footer>
          <Button
            onClick={() => {
              setEmployee(undefined);
              setOibEmployee(null);
              setEditing(null);
              setFilteredEmployees([]);
            }}
            size="sm"
            outline={true}
          >
            {t("common.quit")}
          </Button>
          <Button
            onClick={() => {
              // This changes the state of the editing object, should use dispatch
              editing!.employee =
                employee && employee.id ? employee : undefined;
              editing!.oibEmployee =
                oibEmployee && oibEmployee.lastName ? oibEmployee : null;
              editing!.deactivationDate =
                FormatUtils.formatToISOString(editedActiveTo);

              if (editing!.committeeRole.id === 1) {
                //default for lječnik
                let presidentExist = false;
                tableData.forEach(
                  (el) => (presidentExist = el.isPresident || presidentExist)
                );
                editing!.isPresident = !presidentExist;
              }

              setFilteredEmployees([]);
              setEmployee(undefined);
              setOibEmployee(null);
              setEditedActiveTo(null);
              dispatch(setIsEdited());
              setEditing(null);
              setItem((currItem) =>
                updateCommitteeEmployee(currItem, editing!)
              );
            }}
            size="sm"
          >
            {t("common.save")}
          </Button>
        </BSModal.Footer>
      </BSModal>
    </ContentArea>
  );
}
